<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import InspectorTabsMenu from '@/components/DataTable/InspectorTabsMenu.vue';
import SearchResultInspector from '@/components/DataTable/SearchResultInspector.vue';
import LiveTrackLyricsInspectorContent from '@/components/LiveTrackLyricsInspectorContent.vue';
import LiveTrackUsageInspectorContent from '@/components/LiveTrackUsageInspectorContent.vue';
import HistoryInspectorContent from '@/components/HistoryInspector/HistoryInspectorContent.vue';
import { scopeForTrack } from '@/composables/useHistoryInspector';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The inspector for a Live Track: Details, Playlists, Lyrics and Actions behind
 * the same pull-down a playlist container uses for Details / Switches / Tracks.
 * Playlists lists every bucket and custom list the track is on, with the one
 * button that bans it in all of those zones. Actions is the track's history:
 * every live track action on it, newest first, the same view Track matching's
 * History button opens. Live track actions have no list of their own in the
 * Music sidebar; this tab is where they are found (Joel, 2026-10-17).
 *
 * Lyrics had exactly one door before this — the `lyrics_check_status` table
 * column — so seeing a track's lyrics meant knowing to add that column to the
 * table first. The lyrics view itself is unchanged; this only gives it a way in
 * from the record you are already looking at.
 */
const props = withDefaults(
    defineProps<{
        row?: Record<string, unknown> | null;
        /** Every column of the entity (visible or not) in table order. */
        columns?: TableColumnStaticProps[];
        /** Column ids the header already shows. */
        excludeFields?: string[];
        entityId?: string;
        /** Which section to land on. Ignored when this record has no such section. */
        initialSection?: string | null;
    }>(),
    {
        row: null,
        columns: () => [],
        excludeFields: () => [],
        entityId: 'LiveTrack',
        initialSection: null,
    }
);

/**
 * Playlists and Lyrics are keyed on a numeric LiveTrack id, so a row without a
 * usable one cannot offer those sections at all — better than a section that opens onto an
 * error.
 */
const liveTrackId = computed<number | null>(() => {
    const id = Number(props.row?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
});

/**
 * One list of sections, built in one place, so the switcher and the panels can
 * never disagree about what this record has.
 */
const sections = computed(() => [
    { value: 'details', label: 'Details' },
    ...(liveTrackId.value === null
        ? []
        : [
              { value: 'playlists', label: 'Playlists' },
              { value: 'lyrics', label: 'Lyrics' },
              { value: 'actions', label: 'Actions' },
          ]),
]);

const DEFAULT_SECTION = 'details';
const SECTION_STORAGE_KEY = 'live-track-inspector:tab:v1';

function isOffered(value: string | null | undefined): boolean {
    return sections.value.some((section) => section.value === value);
}

function readPersistedSection(): string {
    try {
        const stored = localStorage.getItem(SECTION_STORAGE_KEY);
        return isOffered(stored) ? (stored as string) : DEFAULT_SECTION;
    } catch {
        return DEFAULT_SECTION;
    }
}

// An explicit request wins over what was last looked at, but only when this
// record actually offers it — a track with no id opened on "lyrics" would
// otherwise land on a section that is not in its list.
const activeSection = ref<string>(
    isOffered(props.initialSection) ? (props.initialSection as string) : readPersistedSection()
);

watch(activeSection, (section) => {
    try {
        localStorage.setItem(SECTION_STORAGE_KEY, section);
    } catch {
        // Private mode / no storage — keep the in-memory choice, just don't persist.
    }
});

// The lyrics-status link on a track card opens this inspector asking for
// "lyrics". When the host reuses one mounted inspector across records, that prop
// changes without a remount, so honour it rather than staying on Details.
watch(
    () => props.initialSection,
    (section) => {
        if (isOffered(section)) {
            activeSection.value = section as string;
        }
    }
);

// Reopening for a different record can drop the section out from under us.
watch(sections, () => {
    if (!isOffered(activeSection.value)) {
        activeSection.value = DEFAULT_SECTION;
    }
});

function onSectionChange(value: string | number) {
    if (isOffered(String(value))) {
        activeSection.value = String(value);
    }
}
</script>

<template>
    <Tabs
        :model-value="activeSection"
        class="flex min-h-0 flex-1 flex-col gap-4"
        data-testid="live-track-inspector"
        @update:model-value="onSectionChange"
    >
        <InspectorTabsMenu
            :model-value="activeSection"
            :items="sections"
            @update:model-value="onSectionChange"
        />

        <TabsContent value="details">
            <SearchResultInspector
                :row="row ?? {}"
                :columns="columns"
                :exclude-fields="excludeFields"
                :entity-id="entityId"
            />
        </TabsContent>

        <TabsContent v-if="liveTrackId !== null" value="playlists" data-testid="live-track-playlists">
            <LiveTrackUsageInspectorContent
                :live-track-id="liveTrackId"
                :track-name="typeof row?.name === 'string' ? row.name : null"
            />
        </TabsContent>

        <TabsContent v-if="liveTrackId !== null" value="lyrics" data-testid="live-track-lyrics">
            <LiveTrackLyricsInspectorContent :live-track-id="liveTrackId" />
        </TabsContent>

        <TabsContent v-if="liveTrackId !== null" value="actions" data-testid="live-track-actions">
            <HistoryInspectorContent seed-kind="track" :initial-filter="scopeForTrack(liveTrackId)" embedded />
        </TabsContent>
    </Tabs>
</template>
